import { AlarmClock, ExternalLink, Image } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import { EmptyWall, RequestErrorWall } from '../components/content-wall';
import { ScheduleCardsSkeleton, ScheduleTabsSkeleton } from '../components/schedule-skeleton';
import { Switch } from '../components/switch';
import { TabBar } from '../components/tab-bar';
import { useApp } from '../components/app-context';
import { t } from '../i18n';
import {
  $currentUser,
  $japaneseAnimeOnly,
  $scheduleFollowingOnly,
  $scheduleSelectedDay,
  $showLateNightAnime,
  setScheduleFollowingOnly,
} from '../store/app.store';
import { useStore } from '../hooks/use-store';
import type {
  AnimeSubject,
  BgmCurrentUser,
  CalendarGroup,
  SubjectBroadcast,
  SubjectCollectionPage,
} from '../types/bgm';
import { safeUrl } from '../utils/url';

const ScheduleSubjectItem = ({ subject }: { subject: AnimeSubject }) => {
  const { navigate } = useApp();
  const image = safeUrl(subject.images?.large ?? subject.images?.common ?? subject.images?.medium);
  return (
    <a
      className="schedule-subject-item flex min-w-0 items-center gap-3 py-1 text-inherit no-underline"
      href={`/subjects/${subject.id}`}
      onClick={(event) => {
        event.preventDefault();
        navigate(`/subjects/${subject.id}`);
      }}
    >
      <div className="schedule-subject-cover">
        {image ? <img src={image} alt="" loading="lazy" referrerPolicy="no-referrer" /> : <Image />}
      </div>
      <div className="schedule-subject-copy grid min-w-0 flex-1 gap-1">
        <strong>{subject.name_cn || subject.name}</strong>
        {subject.name_cn ? <small>{subject.name}</small> : null}
      </div>
    </a>
  );
};

export const SchedulePage = () => {
  const { api, bgm, bgmAuth, locale, showToast } = useApp();
  const [groups, setGroups] = useState<CalendarGroup[]>([]);
  const [broadcasts, setBroadcasts] = useState<SubjectBroadcast[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [followingIds, setFollowingIds] = useState<Set<number>>();
  const [followingLoading, setFollowingLoading] = useState(false);
  const [followingFailed, setFollowingFailed] = useState(false);
  const [retryRevision, setRetryRevision] = useState(0);
  const currentUser = useStore($currentUser);
  const showLateNightAnime = useStore($showLateNightAnime);
  const japaneseAnimeOnly = useStore($japaneseAnimeOnly);
  const followingOnly = useStore($scheduleFollowingOnly);
  const selectedDay = useStore($scheduleSelectedDay);
  const canFilterFollowing = Boolean(currentUser?.bgmToken);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    void bgm<CalendarGroup[]>('/calendar')
      .then(async (result) => {
        const subjectIds = result.flatMap((group) => group.items.flatMap((item) => (item.id ? [item.id] : [])));
        const matchedBroadcasts = await api<SubjectBroadcast[]>('/api/schedule', {
          method: 'POST',
          body: JSON.stringify({ subjectIds }),
        });
        if (!cancelled) {
          setGroups(result.sort((left, right) => left.weekday.id - right.weekday.id));
          setBroadcasts(matchedBroadcasts);
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setFailed(true);
          showToast(error instanceof Error ? error.message : String(error), 'error');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [api, bgm, retryRevision, showToast]);

  useEffect(() => {
    if (!followingOnly || !canFilterFollowing) {
      setFollowingIds(undefined);
      setFollowingLoading(false);
      setFollowingFailed(false);
      return;
    }
    let cancelled = false;
    setFollowingIds(undefined);
    setFollowingLoading(true);
    setFollowingFailed(false);
    void (async () => {
      const username = (await bgmAuth<BgmCurrentUser>('/v0/me')).username;
      const ids = new Set<number>();
      const limit = 100;
      let offset = 0;
      while (true) {
        const result = await bgmAuth<SubjectCollectionPage>(
          `/v0/users/${encodeURIComponent(username)}/collections?subject_type=2&type=3&limit=${limit}&offset=${offset}`,
        );
        for (const collection of result.data) {
          ids.add(collection.subject.id);
        }
        offset += result.data.length;
        if (result.data.length === 0 || offset >= result.total) {
          break;
        }
      }
      if (!cancelled) {
        setFollowingIds(ids);
      }
    })()
      .catch((error: unknown) => {
        if (!cancelled) {
          setFollowingFailed(true);
          showToast(error instanceof Error ? error.message : String(error), 'error');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setFollowingLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [bgmAuth, canFilterFollowing, followingOnly, retryRevision, showToast]);

  const timeGroups = useMemo(() => {
    const broadcastsBySubject = new Map(broadcasts.map((broadcast) => [broadcast.subjectId, broadcast]));
    const formatter = new Intl.DateTimeFormat(locale, {
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    });
    const grouped = new Map<string, Map<string, AnimeSubject[]>>();
    const subjectsForDay = (day: number): AnimeSubject[] => {
      const group = groups.find((item) => item.weekday.id === day);
      return (group?.items ?? [])
        .filter((item): item is typeof item & { id: number; name: string } => Boolean(item.id && item.name))
        .map((item) => ({ ...item, id: item.id, name: item.name }))
        .filter((subject) => !japaneseAnimeOnly || broadcastsBySubject.get(subject.id)?.language === 'ja')
        .filter((subject) => !followingOnly || !canFilterFollowing || followingIds?.has(subject.id));
    };
    const broadcastTimeFor = (subject: AnimeSubject): string => {
      const broadcastAt = broadcastsBySubject.get(subject.id)?.broadcastAt;
      const date = broadcastAt ? new Date(broadcastAt) : undefined;
      return date && !Number.isNaN(date.getTime()) ? formatter.format(date) : '';
    };
    const addSubject = (subject: AnimeSubject, broadcastTime: string): void => {
      const hour = broadcastTime ? `${broadcastTime.split(':')[0]}:00` : '';
      const slots = grouped.get(hour) ?? new Map<string, AnimeSubject[]>();
      const items = slots.get(broadcastTime) ?? [];
      items.push(subject);
      slots.set(broadcastTime, items);
      grouped.set(hour, slots);
    };
    for (const subject of subjectsForDay(selectedDay)) {
      const broadcastTime = broadcastTimeFor(subject);
      const hourValue = Number(broadcastTime.split(':')[0]);
      if (!showLateNightAnime || !broadcastTime || hourValue >= 2) {
        addSubject(subject, broadcastTime);
      }
    }
    if (showLateNightAnime) {
      const nextDay = selectedDay === 7 ? 1 : selectedDay + 1;
      for (const subject of subjectsForDay(nextDay)) {
        const broadcastTime = broadcastTimeFor(subject);
        const [hourText, minuteText] = broadcastTime.split(':');
        const hourValue = Number(hourText);
        if (broadcastTime && hourValue < 2) {
          addSubject(subject, `${hourValue + 24}:${minuteText}`);
        }
      }
    }
    return [...grouped.entries()]
      .sort(([left], [right]) => (left ? (right ? left.localeCompare(right) : -1) : 1))
      .map(([time, slots]) => ({
        time,
        slots: [...slots.entries()]
          .sort(([left], [right]) => left.localeCompare(right))
          .map(([broadcastTime, items]) => ({
            broadcastTime,
            items: items.sort((left, right) => (left.name_cn || left.name).localeCompare(right.name_cn || right.name)),
          })),
      }));
  }, [
    broadcasts,
    canFilterFollowing,
    followingIds,
    followingOnly,
    groups,
    japaneseAnimeOnly,
    locale,
    selectedDay,
    showLateNightAnime,
  ]);

  const pageFailed = failed || followingFailed;
  const pageLoading = loading || (followingOnly && canFilterFollowing && followingLoading);

  return (
    <section>
      <div className="hero-copy compact-hero">
        <p className="eyebrow">CALENDAR</p>
        <h1>{t('schedule')}</h1>
        <a
          className="schedule-data-source"
          href="https://github.com/bangumi-data/bangumi-data"
          target="_blank"
          rel="noreferrer"
        >
          <span>{t('scheduleDataSource')}</span>
          <ExternalLink />
        </a>
        {canFilterFollowing ? (
          <div className="mt-4 flex items-center gap-2.5">
            <span className="text-xs text-(--muted)">{t('scheduleFollowingOnly')}</span>
            <Switch label={t('scheduleFollowingOnly')} checked={followingOnly} onChange={setScheduleFollowingOnly} />
          </div>
        ) : null}
      </div>
      {pageFailed ? null : loading ? (
        <div className="tab-bar schedule-tabs">
          <ScheduleTabsSkeleton />
        </div>
      ) : (
        <TabBar
          className="schedule-tabs"
          items={groups.map((group) => ({
            value: group.weekday.id,
            label: locale === 'zh-CN' ? group.weekday.cn : group.weekday.en,
          }))}
          value={selectedDay}
          onChange={(value) => $scheduleSelectedDay.set(Number(value))}
        />
      )}
      {pageFailed ? (
        <RequestErrorWall onRetry={() => setRetryRevision((current) => current + 1)} />
      ) : pageLoading ? (
        <div className="grid gap-6">
          <ScheduleCardsSkeleton />
        </div>
      ) : timeGroups.length === 0 ? (
        <EmptyWall message={followingOnly && canFilterFollowing ? t('noContent') : undefined} />
      ) : (
        <div className="grid gap-6">
          {timeGroups.map((group) => (
            <section className="schedule-time-group grid gap-3" key={group.time || 'unknown'}>
              <time>
                <AlarmClock />
                <span>{group.time || t('airTimeUnknown')}</span>
              </time>
              <div className="grid gap-4">
                {group.slots.map((slot) => (
                  <div className="grid gap-2" key={slot.broadcastTime || 'unknown'}>
                    {slot.broadcastTime ? (
                      <small className="schedule-broadcast-time">{slot.broadcastTime}</small>
                    ) : null}
                    <div className="grid gap-2.5">
                      {slot.items.map((subject) => (
                        <ScheduleSubjectItem subject={subject} key={subject.id} />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </section>
  );
};
